import React, { useState, useEffect } from 'react';
import { Globe, Search, Filter, CheckCircle, ExternalLink, RefreshCw, FileText, X, ChevronLeft, ChevronRight, XCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';


const ENGINE_ICONS = {
  /* INJECTED_MB_PC_LOGOS_v2 */
  '豆包': '/icons/doubao.png',
  '豆包(PC)': '/icons/doubao.png',
  '豆包(MB)': '/icons/doubao.png',
  '豆包（PC）': '/icons/doubao.png',
  '豆包（MB）': '/icons/doubao.png',
  'doubao': '/icons/doubao.png',
  '腾讯元宝(PC)': '/icons/yuanbao.png',
  '腾讯元宝(MB)': '/icons/yuanbao.png',
  '腾讯元宝（PC）': '/icons/yuanbao.png',
  '腾讯元宝（MB）': '/icons/yuanbao.png',
  '通义千问(PC)': '/icons/tongyi.png',
  '通义千问(MB)': '/icons/tongyi.png',
  '通义千问（PC）': '/icons/tongyi.png',
  '通义千问（MB）': '/icons/tongyi.png',
  '通义': '/icons/tongyi.png',
  '文心一言': '/icons/wenxin.png',
  '文心一言(PC)': '/icons/wenxin.png',
  '文心一言(MB)': '/icons/wenxin.png',
  'Deepseek': '/icons/deepseek.png',
  'DeepSeek': '/icons/deepseek.png',
  'deepseek': '/icons/deepseek.png',
  'Kimi': '/icons/kimi.png',
  'kimi': '/icons/kimi.png',
  '元宝': '/icons/yuanbao.png',
  '腾讯元宝': '/icons/yuanbao.png',
  '百度AI搜索': '/icons/baidu.png',
  '百度': '/icons/baidu.png',
  '小红书AI': '/icons/xiaohongshu.png',
  '抖音AI': '/icons/douyin.png',
  '小红书': '/icons/xiaohongshu.png',
  'xhs': '/icons/xiaohongshu.png',
  '微信搜一搜': '/icons/wechat.png',
  '微信': '/icons/wechat.png',
};

const getEngineIcon = (name) => {
  if (!name) return '/icons/missing.png';
  return ENGINE_ICONS[name] || `/icons/${name.toLowerCase()}.png`;
};

export default function CorpusCrawl() {
  const { user } = useAuth();
  const [crawledData, setCrawledData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Viewer Modal
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Adopted state tracking
  const [adoptedStatusMap, setAdoptedStatusMap] = useState(new Map());
  const [processingIds, setProcessingIds] = useState(new Set());

  useEffect(() => {
    fetchCrawledData();
    window.addEventListener('globalFilterChange', fetchCrawledData);
    return () => window.removeEventListener('globalFilterChange', fetchCrawledData);
  }, []);

  const fetchCrawledData = async () => {
    const projectId = localStorage.getItem('globalBrand');
    if (!projectId || projectId === 'all' || projectId === 'undefined') {
      setCrawledData([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      // Fetch both results and currently adopted items to sync state
      const [res, corpusRes] = await Promise.all([
        axios.get(`/api/results?brandProjectId=${projectId}&limit=1000`),
        axios.get(`/api/corpus/items?projectId=${projectId}&limit=1000`) // Assuming this route can return list
      ]);
      
      const rawResults = res.data.data || [];
      const adoptedCorpus = corpusRes.data.items || [];
      const adoptedUrlsMap = new Map(adoptedCorpus.map(c => [c.sourceUrl, c.status]));

      let citations = [];
      
      rawResults.forEach(r => {
        const sources = r.geoResultSources || r.georesultsource || r.mediaSources || [];
        if (sources && sources.length > 0) {
          sources.forEach((c, idx) => {
             if (c.title && c.url) {
                citations.push({
                  id: `${r.id}-${c.id || idx}`,
                  title: c.title,
                  url: c.url,
                  snippet: c.txtContent || '该内容在搜索结果中被引用...',
                  sourceEngine: r.engineName || r.engineId,
                  searchQuery: r.question || '未知关键词',
                  sentiment: r.sentiment || 'neutral',
                  date: r.createdAt
                });
             }
          });
        }
      });
      
      const uniqueCitations = [];
      const urlMap = new Map();
      citations.forEach(c => {
        if (!urlMap.has(c.url)) {
          urlMap.set(c.url, true);
          uniqueCitations.push(c);
        }
      });

      setCrawledData(uniqueCitations);
      
      // Mark initially adopted
      const initialStatusMap = new Map();
      uniqueCitations.forEach(c => {
        if (adoptedUrlsMap.has(c.url)) {
          initialStatusMap.set(c.id, adoptedUrlsMap.get(c.url));
        }
      });
      setAdoptedStatusMap(initialStatusMap);
      setCurrentPage(1);
    } catch (err) {
      console.error('Failed to fetch crawled data:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleAdopt = async (item, e) => {
    if (e) e.stopPropagation();
    
    if (user?.role !== 'ADMIN' && !user?.permissions?.includes('corpus:edit')) {
      return alert('您没有语料采纳/编辑权限');
    }

    const isAdopted = adoptedStatusMap.has(item.id);
    
    if (isAdopted) {
      return alert('该文章已采纳入库。如需撤回或彻底删除，请联系语料库管理员在【结构化语料池】中操作。');
    }

    const projectId = localStorage.getItem('globalBrand');
    if (!projectId || projectId === 'all' || projectId === 'undefined') {
      return alert('请先选择具体的品牌项目');
    }

    setProcessingIds(prev => new Set([...prev, item.id]));

    try {
      // 对于生态溯源采纳，使用特殊的 sourceType: 'ECO_LINK'
      const response = await axios.post('/api/corpus/items', {
        projectId: projectId,
        title: item.title,
        sourceType: 'ECO_LINK',
        rawContent: item.snippet || item.title,
        sourceUrl: item.url,
        creatorId: user?.userId || user?.id
      });
      
      setAdoptedStatusMap(prev => {
        const newMap = new Map(prev);
        newMap.set(item.id, response.data.status || 'PENDING');
        return newMap;
      });
    } catch (err) {
      console.error(err);
      alert('采纳失败');
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    }
  };

  const [hideAdopted, setHideAdopted] = useState(true);

  const filteredData = crawledData.filter(item => {
    const matchSearch = item.title.toLowerCase().includes(search.toLowerCase()) || 
                        item.searchQuery.toLowerCase().includes(search.toLowerCase());
    const isAdopted = adoptedStatusMap.has(item.id);
    const matchStatus = hideAdopted ? !isAdopted : true;
    return matchSearch && matchStatus;
  });

  const adoptedCount = crawledData.length - filteredData.length;

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const currentData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const openViewer = (item) => {
    const index = filteredData.findIndex(p => p.id === item.id);
    setSelectedIndex(index);
    setViewerOpen(true);
  };

  const nextItem = () => {
    if (selectedIndex < filteredData.length - 1) setSelectedIndex(selectedIndex + 1);
  };

  const prevItem = () => {
    if (selectedIndex > 0) setSelectedIndex(selectedIndex - 1);
  };

  return (
    <div className="p-6 h-full flex flex-col">
      <div className="flex justify-between items-center mb-6 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Globe className="w-6 h-6 text-indigo-500" />
            生态溯源回流
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            浏览由AI搜索引擎高频引用的全网媒体内容，一键采纳优质内容补充品牌语料库。
          </p>
        </div>
        <button 
          onClick={fetchCrawledData}
          className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          刷新公域池
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 p-6 flex-1 flex flex-col min-h-0">
        <div className="flex flex-wrap items-center gap-4 mb-6 shrink-0">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="搜索标题、关键词..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 dark:text-white transition-shadow"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <button 
            onClick={() => {
              setHideAdopted(!hideAdopted);
              setCurrentPage(1);
            }}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors font-medium text-sm ${hideAdopted ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800/50' : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700'}`}
          >
            <Filter className="w-4 h-4" /> 
            {hideAdopted ? '已隐藏沉淀文章' : '显示所有历史文章'}
          </button>
          <div className="text-sm px-4 py-2 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400">
            新增待审阅: <span className="font-bold text-indigo-600 dark:text-indigo-400">{filteredData.length}</span> 篇
            {hideAdopted && adoptedCount > 0 && <span className="ml-2 text-xs opacity-80">(已自动隐藏 {adoptedCount} 篇历史采纳)</span>}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center flex-1">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-16 flex-1 flex flex-col justify-center items-center">
            <Globe className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">生态池暂无数据</h3>
            <p className="text-gray-500 mt-1">当前项目暂时没有捕获到新的外部引用源，或者没有匹配搜索的结果。</p>
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 overflow-y-auto pb-4 pr-2">
              {currentData.map((item) => {
                const isAdopted = adoptedStatusMap.has(item.id);
                const status = adoptedStatusMap.get(item.id);
                const isProcessing = processingIds.has(item.id);
                return (
                <div 
                  key={item.id} 
                  onClick={() => openViewer(item)}
                  className={`border ${isAdopted ? 'border-indigo-300 dark:border-indigo-600/50 bg-indigo-50/30 dark:bg-indigo-900/10' : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800'} rounded-xl p-4 hover:shadow-md transition-all cursor-pointer flex flex-col h-[200px]`}
                >
                  <div className="flex justify-between items-start mb-2 gap-2">
                    <h3 className={`font-semibold text-sm line-clamp-2 leading-snug flex-1 ${isAdopted ? 'text-indigo-800 dark:text-indigo-300' : 'text-gray-900 dark:text-white hover:text-indigo-600'}`}>
                      {item.title}
                    </h3>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-3 shrink-0">
                    <span className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs rounded font-medium border border-slate-200 dark:border-slate-600">
                      <img src={getEngineIcon(item.sourceEngine)} alt={item.sourceEngine} onError={(e) => { e.target.onerror = null; e.target.src = "/icons/missing.png"; }} className="w-3.5 h-3.5 inline-block mr-1 rounded-sm object-contain" /> {item.sourceEngine}
                    </span>
                    <span className="text-xs text-gray-500 truncate" title={item.searchQuery}>
                      {item.searchQuery}
                    </span>
                  </div>
                  
                  <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-3 mb-auto">
                    {item.snippet}
                  </p>
                  
                  <div className="flex items-center justify-end pt-3 mt-3 border-t border-gray-100 dark:border-slate-700 shrink-0">
                    <button 
                      onClick={(e) => toggleAdopt(item, e)}
                      disabled={isAdopted || isProcessing}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                        isProcessing
                          ? 'bg-amber-100 text-amber-700 border border-amber-200 cursor-wait dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'
                          : isAdopted 
                            ? (status === 'APPROVED' ? 'bg-green-100 text-green-700 border border-green-200 cursor-not-allowed dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' : 'bg-gray-100 text-gray-600 border border-gray-200 cursor-not-allowed dark:bg-slate-700 dark:border-slate-600 dark:text-gray-400')
                            : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm shadow-indigo-600/20'
                      }`}
                    >
                      {isProcessing ? (
                        <>
                          <span className="w-3.5 h-3.5 border-2 border-amber-600 border-t-transparent rounded-full animate-spin"></span>
                          AI 入库分析中...
                        </>
                      ) : isAdopted ? (
                        status === 'APPROVED' ? (
                          <><CheckCircle className="w-3.5 h-3.5" /> 已采纳 (过审)</>
                        ) : (
                          <><CheckCircle className="w-3.5 h-3.5" /> 已入库 (待审核)</>
                        )
                      ) : (
                        <>
                          <CheckCircle className="w-3.5 h-3.5" />
                          采纳入库
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )})}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-slate-700 shrink-0">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  显示 {(currentPage - 1) * itemsPerPage + 1} 到 {Math.min(currentPage * itemsPerPage, filteredData.length)} 条，共 {filteredData.length} 条
                </span>
                <div className="flex gap-1">
                  <button 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    className="p-2 border border-gray-200 dark:border-slate-600 rounded disabled:opacity-50 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                    {currentPage} / {totalPages}
                  </span>
                  <button 
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    className="p-2 border border-gray-200 dark:border-slate-600 rounded disabled:opacity-50 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Viewer Modal */}
      {viewerOpen && filteredData[selectedIndex] && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <span className="bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-600 px-2 py-1 rounded text-xs font-semibold">
                  <img src={getEngineIcon(filteredData[selectedIndex].sourceEngine)} alt={filteredData[selectedIndex].sourceEngine} onError={(e) => { e.target.onerror = null; e.target.src = "/icons/missing.png"; }} className="w-4 h-4 inline-block mr-1.5 rounded-sm object-contain" /> {filteredData[selectedIndex].sourceEngine}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  触发词: {filteredData[selectedIndex].searchQuery}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <a 
                  href={filteredData[selectedIndex].url} 
                  target="_blank" 
                  rel="noreferrer"
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded transition-colors"
                >
                  <ExternalLink className="w-4 h-4" /> 访问原文
                </a>
                <button 
                  onClick={() => setViewerOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-8 relative">
              {/* Prev Button */}
              <button 
                onClick={prevItem}
                disabled={selectedIndex === 0}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white dark:bg-slate-700 shadow-md border border-gray-100 dark:border-slate-600 text-gray-500 disabled:opacity-30 hover:text-indigo-600 hover:border-indigo-200 transition-all z-10"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <div className="max-w-2xl mx-auto px-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 leading-relaxed">
                  {filteredData[selectedIndex].title}
                </h2>
                
                <div className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-loose">
                  {filteredData[selectedIndex].snippet.split('\n').map((para, i) => (
                    <p key={i} className="mb-4">{para}</p>
                  ))}
                  
                  {filteredData[selectedIndex].snippet.length < 50 && (
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200 rounded-lg text-sm flex items-start gap-2 mt-8">
                      <FileText className="w-5 h-5 shrink-0" />
                      <p>当前记录仅抓取到少量摘要片段，建议点击右上角「访问原文」获取完整内容，或直接采纳入库后由后台引擎深度解析原文链接。</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Next Button */}
              <button 
                onClick={nextItem}
                disabled={selectedIndex === filteredData.length - 1}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white dark:bg-slate-700 shadow-md border border-gray-100 dark:border-slate-600 text-gray-500 disabled:opacity-30 hover:text-indigo-600 hover:border-indigo-200 transition-all z-10"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                记录 {selectedIndex + 1} / {filteredData.length}
              </div>
              <div className="flex gap-3">
                <button 
                  onClick={() => toggleAdopt(filteredData[selectedIndex])}
                  disabled={adoptedStatusMap.has(filteredData[selectedIndex].id) || processingIds.has(filteredData[selectedIndex].id)}
                  className={`px-6 py-2.5 flex items-center gap-2 rounded-lg font-medium transition-all ${
                    processingIds.has(filteredData[selectedIndex].id)
                      ? 'bg-amber-100 text-amber-700 border border-amber-200 cursor-wait dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800'
                      : adoptedStatusMap.has(filteredData[selectedIndex].id)
                        ? (adoptedStatusMap.get(filteredData[selectedIndex].id) === 'APPROVED' ? 'bg-green-100 text-green-700 border border-green-200 cursor-not-allowed dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' : 'bg-gray-100 text-gray-600 border border-gray-200 cursor-not-allowed dark:bg-slate-700 dark:border-slate-600 dark:text-gray-400')
                        : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-600/30 hover:shadow-indigo-600/40'
                  }`}
                >
                  {processingIds.has(filteredData[selectedIndex].id) ? (
                    <>
                      <span className="w-5 h-5 border-2 border-amber-600 border-t-transparent rounded-full animate-spin"></span> AI 入库分析中...
                    </>
                  ) : adoptedStatusMap.has(filteredData[selectedIndex].id) ? (
                    adoptedStatusMap.get(filteredData[selectedIndex].id) === 'APPROVED' ? (
                      <><CheckCircle className="w-5 h-5" /> 已采纳 (过审)</>
                    ) : (
                      <><CheckCircle className="w-5 h-5" /> 已入库 (待审核)</>
                    )
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" /> 采纳入库
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
